import { useApolloClient } from '@apollo/client'
import { Loader, RefreshCw } from 'lucide-react'
import { useRef, useTransition } from 'react'
import { useParams } from 'react-router'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type { FilterType } from '@/routes/webhook/tabs/event-deliveries'
import type {
  WebhookNewDeliveriesQuery,
  WebhookNewDeliveriesQueryVariables,
} from '@/routes/webhook/tabs/event-deliveries/event-delivery-list/new-events-indicator/index.types.gen'

const WEBHOOK_NEW_EVENTS = gql`
  query WebhookNewDeliveries(
    $idCompany: String!
    $id: String!
    $filters: JSON
  ) {
    company(id: $idCompany) {
      id
      webhook(id: $id) {
        id
        eventDeliveries(filters: $filters) @live {
          id
          total
        }
      }
    }
  }
`

type Props = {
  filters: FilterType
}

export const NewEventsIndicator = ({ filters }: Props) => {
  const [isPending, startTransition] = useTransition()
  const { idWebhook = '' } = useParams()
  const { idCompany } = useIdCompany()
  const client = useApolloClient()

  const { data, refetch } = useSuspenseQuery<
    WebhookNewDeliveriesQuery,
    WebhookNewDeliveriesQueryVariables
  >(WEBHOOK_NEW_EVENTS, {
    variables: {
      idCompany,
      id: idWebhook,
      filters,
    },
  })

  const newTotal = data?.company?.webhook?.eventDeliveries?.total || 0
  const initialTotalRef = useRef<number | null>(null)
  if (initialTotalRef.current === null && newTotal > 0) {
    initialTotalRef.current = newTotal
  }

  const initialTotal = initialTotalRef.current ?? 0
  const diff = newTotal - initialTotal

  return diff > 0 ? (
    <div className="flex items-center gap-2">
      <Muted>
        {diff} new {diff === 1 ? 'event' : 'events'} available
      </Muted>
      <Button
        size="sm"
        disabled={isPending}
        variant="outline"
        onClick={() => {
          startTransition(async () => {
            try {
              await client.refetchQueries({
                include: ['WebhookDeliveries'],
              })
              initialTotalRef.current = null
              await refetch()
            } catch (e) {
              console.log(e)
            }
          })
        }}
      >
        {isPending ? (
          <Loader className="animate-spin" aria-hidden="true" />
        ) : (
          <RefreshCw aria-hidden="true" />
        )}
        Refresh
      </Button>
    </div>
  ) : null
}
